import { Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useEffect } from 'react';

import { trackWebPushPromptView } from '#features/analytics/utils/pushPermissionFunnel';
import { Button } from '#features/ui';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '#features/ui/components/Dialog';
import { useWebPushPrompt } from '../../hooks/useWebPushPrompt';
import type { PushAttentionReason } from '../../utils/pushAttention';

/**
 * Считает показ.
 *
 * Отдельным компонентом по той же причине, что и в нативной шторке: условий видимости с
 * десяток, и единственная честная точка отсчёта это факт, что разметка смонтировалась.
 */
const VisibilityBeacon = ({ reason }: { reason: PushAttentionReason }) => {
  useEffect(() => {
    trackWebPushPromptView(reason);
  }, [reason]);

  return null;
};

/**
 * Просьба включить уведомления в вебе.
 *
 * Появляется не при загрузке страницы, а после действия, ради которого уведомления и нужны:
 * человек отправил сообщение, создал заявку, откликнулся или вошёл в сессию, то есть ждёт
 * ответа. До 06.09.2026 браузерный диалог поднимался прямо на загрузке, у каждого
 * залогиненного и без единого слова объяснения. Отказ в Chrome это настройка сайта навсегда, так
 * что цена такой просьбы не «одно закрытое окно», а человек, до которого мы больше никогда не
 * достучимся: на проде токен есть у 18% недельных активных, при том что дошедший пуш поднимает
 * возврат в первый час с 7.6% до 22%.
 *
 * Всё решение лежит в `useWebPushPrompt`, здесь только разметка. Диалог берётся общий: ниже 768px
 * он сам становится шторкой снизу, а Escape, ловушку фокуса, блокировку прокрутки и отступ под
 * системную панель приносит с собой (у нативной шторки всего этого нет, она рисует голый
 * фиксированный блок).
 */
export const WebPushPrompt = () => {
  const { t } = useLingui();
  const { allow, dismiss, later, reason } = useWebPushPrompt();

  if (!reason) {
    return null;
  }

  return (
    <DialogRoot
      open
      onOpenChange={(event) => {
        if (!event.open) {
          dismiss();
        }
      }}
      // Фокус не отдаём никакой кнопке: случайный Enter не должен поднимать диалог браузера.
      initialFocusEl="body"
    >
      <DialogContent>
        <VisibilityBeacon reason={reason} />
        <DialogHeader title={t`Know when someone answers`} closable />
        <DialogBody>
          <Text textStyle="body" color="fg.secondary">
            {/* Последняя фраза и есть смысл всей карточки: она снимает рефлекторное
                «Блокировать», которое в Chrome навсегда. */}
            {t`Turn on notifications and we will tell you the moment a reply or an invite arrives, even when this tab is closed. Your browser will ask next, choose Allow.`}
          </Text>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={later}>
            {t`Not now`}
          </Button>
          <Button onClick={allow}>{t`Allow notifications`}</Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
